import { isCurrentLocaleCommaDecimalSeparator } from "@/react/helpers/localeHelpers";
import { formatPaywallCurrencyAmount } from "@/react/helpers/paywalls/currencies";
import type { PaywallCurrency } from "@/react/types/Paywall";

const PAGINATION_LIMIT_THRESHOLD = 1000;

const SEARCH_INVALID_CHARACTERS = /[^\d.-]/g;
const SEARCH_DOTS = /\./g;

export const getPaginationLimitThreshold = (value: string | number): number =>
  Math.min(Number(value) || 1, PAGINATION_LIMIT_THRESHOLD);

export const mapCurrencyAmountStrToInt = (
  currencyAmountStr: string | number,
): number =>
  parseInt(String(currencyAmountStr || "0").replace(/[^\d\\-]/g, ""));

export const mapCurrencyAmountStrToDecimal = (
  currencyAmountStr: string | number,
): number => {
  const str = String(currencyAmountStr || "0");
  if (isCurrentLocaleCommaDecimalSeparator()) {
    return parseFloat(
      str
        .replace(SEARCH_DOTS, "")
        .replace(",", ".")
        .replace(SEARCH_INVALID_CHARACTERS, ""),
    );
  }
  return parseFloat(str.replace(SEARCH_INVALID_CHARACTERS, ""));
};

interface TransformInputValueOptions {
  amountInCents?: boolean;
}

const getMaximumAmount = (
  maximumAmount: number | undefined,
  amount: number,
  amountInCents: boolean,
) => {
  if (!maximumAmount || isNaN(maximumAmount)) {
    return amount;
  }

  let maxValueAllowed = maximumAmount;
  if (!amountInCents) {
    maxValueAllowed = Math.round(maximumAmount / 100);
  }
  return Math.min(amount, maxValueAllowed);
};

export const buildTransformInputValueToCurrencyAmountStrFunc = (
  currency: Pick<
    PaywallCurrency,
    "smallest_unit" | "code" | "symbol" | "direction"
  > &
    Partial<PaywallCurrency>,
  options: TransformInputValueOptions = {},
) => {
  const defaultOpts: Required<TransformInputValueOptions> = {
    amountInCents: true,
  };
  const opts = { ...defaultOpts, ...options };

  return (inputValue: string | number): string | undefined => {
    let amount: number;
    if (opts.amountInCents) {
      amount = mapCurrencyAmountStrToInt(inputValue);
    } else {
      amount = mapCurrencyAmountStrToDecimal(inputValue);
    }
    if (!isNaN(amount)) {
      amount = getMaximumAmount(
        currency.max_charge_amount,
        amount,
        !!opts.amountInCents,
      );

      return formatPaywallCurrencyAmount(currency, amount, {
        amountInCents: opts.amountInCents,
        includeCurrencyCode: false,
        forceMaxFractionDigits: true,
      });
    }
    return undefined;
  };
};
